import { Fragment, type ReactNode, useEffect, useMemo } from 'react'
import type { AddPrizeRequestBody, PeriodType, PrizeAddFields, PrizeType } from './PrizeAddForm.types'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { axiosPost } from 'connectors/axiosPost'
import { useMutation } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Input from 'components/Input'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Select from 'components/Select'
import type { Market } from 'types/Market'
import RequestStatus from 'components/RequestStatus'
import PhysicalFields from './PhysicalFields'
import { excelExt } from 'config/extensions'
import { useNavigate } from 'react-router-dom'
import type { ApiLanguage } from 'pages/Markets/Form/MarketForm.types'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import { useAppContext } from 'context/AppContext/AppContext'
import FileUpload from 'components/FileUpload'
import { useAlert } from 'context/AlertContext'
import useQueryGet from 'hooks/useQueryGet'
import { Divider, Paper } from '@mui/material'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import PageLoader from 'components/PageLoader'

const renderDate = (date: Date): [number, number, number] => {
  const day = date.getDate()
  const month = date.getMonth()
  const year = date.getFullYear()

  return [year, month, day]
}

const PrizeAddForm = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { changeMessage } = useAlert()
  const { markets, isMarketsLoading } = useAppContext()
  const { token, refreshToken } = useAuthContext()

  const prizeTypeMock: PrizeType[] = [
    {
      label: t('prize.physical'),
      value: 'physical',
    },
    {
      label: t('prize.files'),
      value: 'files',
    },
    {
      label: t('prize.digitalCodes'),
      value: 'digital-codes',
    },
    {
      label: t('prize.digitalLinks'),
      value: 'digital-links',
    },
  ]

  const periodsMock: PeriodType[] = [
    {
      label: t('prize.periodDay'),
      value: 'day',
    },
    {
      label: t('prize.workingDay'),
      value: 'working-day',
    },
    {
      label: t('prize.periodWeek'),
      value: 'week',
    },
    {
      label: t('prize.periodMonth'),
      value: 'month',
    },
    {
      label: t('prize.periodWhole'),
      value: 'whole-promo',
    },
  ]

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t('forms.required', { field: t('common.name') }) ?? 'Required')
      .matches(/^[A-Za-z_-]+$/, t('forms.fieldSlug') ?? 'only [A-Za-z_-]'),
    market: Yup.object<Market>().required(t('forms.required', { field: t('prize.market') }) ?? 'Required'),
    periods: Yup.array()
      .of(
        Yup.object().shape({
          startDate: Yup.date(),
          endDate: Yup.date(),
          amount: Yup.string(),
        })
      )
      .test('periodTotal', t('forms.prizesAmount') ?? '', (item, { parent }) => {
        const totalAmount = parent.totalAmount
        if (parent.period === 'day' || parent.period === 'working-day' || parent.period === 'whole-promo') {
          return true
        }

        if (totalAmount !== undefined) {
          const sumOfPrizes = item?.reduce((a, b) => a + Number(b.amount), 0)

          return Number(totalAmount) === sumOfPrizes
        }

        return false
      }),
    totalAmount: Yup.number()
      .typeError(t('forms.totalPrizesAmount') ?? '')
      .test('type', t('forms.required', { field: t('prize.periodTotal') }) ?? '', (item, { parent }) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return parent.type.includes('digital') || (item !== undefined && item > 0)
      }),
    file: Yup.mixed()
      .nullable()
      .test('file', t('forms.prizesFile') ?? '', (value, { parent }) => {
        if (['physical', 'files'].includes(parent.type)) {
          return true
        }

        if (value === null) {
          return false
        }

        return true
      }),
    translations: Yup.lazy(value => {
      if (value !== undefined) {
        const newEntries = Object.keys(value).reduce(
          (acc, key) => ({
            ...acc,
            [key]: Yup.string().required(),
          }),
          {}
        )
        return Yup.object().shape(newEntries).required()
      }
      return Yup.mixed().notRequired()
    }),
    descriptions: Yup.lazy(value => {
      if (value !== undefined) {
        const newEntries = Object.keys(value).reduce(
          (acc, key) => ({
            ...acc,
            [key]: Yup.string().max(500),
          }),
          {}
        )
        return Yup.object().shape(newEntries)
      }
      return Yup.mixed().notRequired()
    }),
  })

  const defaultValues: PrizeAddFields = {
    name: '',
    market: null,
    type: 'physical',
    totalAmount: 0,
    period: 'day',
    descriptions: {},
    periods: [],
    file: null,
    translations: {},
    emailWinnerContents: {},
    rewardsPerIssue: '1',
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
    formState: { errors },
    setError,
  } = useForm<PrizeAddFields>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    mutate: addPrize,
    data: responseData,
    status,
    reset,
    isLoading,
  } = useMutation(async (prize: FormData) => await axiosPost<any>(token, refreshToken, 'prizes', prize), {
    onSuccess: data => {
      if (data.id !== undefined) {
        changeMessage(t('common.success'), 'success', () => {
          reset()
        })
        navigate('/prizes-groups')
      }
    },
  })

  const { data: languages } = useQueryGet<ApiLanguage[]>({
    queryKey: ['markets-languages', 'prize-translations-langs', watch('market')],
    endpoint: `market-languages/${watch('market.id')}`,
    queryOptions: { enabled: watch('market') !== null },
  })

  console.log('errors', errors)

  useEffect(() => {
    if (errors.periods?.message !== undefined) {
      changeMessage(errors.periods.message, 'error', () => {
        setError('periods', {})
      })
    }
    if (responseData?.error?.data.message !== undefined) {
      changeMessage(responseData.error.data.message, 'error', reset)
    }
  }, [errors, responseData])

  useEffect(() => {
    if (status === 'success' && responseData !== undefined && !('error' in responseData)) {
      navigate('/prizes-groups')
    }
  }, [responseData, status])

  const typeFields = useMemo(() => {
    const market = watch('market')
    if (market === null || market.flow === 'pd') {
      return null
    }

    return (
      <PhysicalFields
        market={market}
        period={watch('period')}
        prizesTotal={watch('totalAmount')}
        control={control}
        setValue={setValue}
        getValues={getValues}
      />
    )
  }, [watch('type'), watch('period'), watch('periods'), watch('market'), watch('totalAmount')])

  const translations = useMemo((): ReactNode => {
    const selectedMarket = watch('market')

    if (languages === undefined || selectedMarket === null) {
      return null
    }

    if ('error' in languages) {
      return <RequestStatus data={languages} />
    }

    setValue('translations', {})
    languages.forEach(lang => {
      setValue(`translations.${lang.id}`, '')
    })

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography>{t('navigation.translations')}</Typography>
        {languages.map(lang => (
          <Fragment key={lang.id}>
            <Input<PrizeAddFields>
              name={`translations.${lang.id}`}
              label={`${t('common.translation')} - ${lang.name} | ${lang.nativeName}`}
              control={control}
              textInputProps={{ sx: { mt: 1 } }}
            />
            <Input<PrizeAddFields>
              name={`descriptions.${lang.id}`}
              textInputProps={{ multiline: true, rows: 5 }}
              label={`${t('common.description')} - ${lang.name} | ${lang.nativeName}`}
              control={control}
            />
            <Input<PrizeAddFields>
              name={`emailWinnerContents.${lang.id}`}
              textInputProps={{ multiline: true, rows: 5 }}
              label={`${t('prize.emailWinnerContent')} - ${lang.name} | ${lang.nativeName}`}
              control={control}
            />
          </Fragment>
        ))}
      </Box>
    )
  }, [languages])

  const onHandleSubmit = async (data: PrizeAddFields): Promise<any> => {
    if (data.market === null) {
      return
    }
    const { market, ...restData } = data
    const fd = new FormData()
    const requestBody: AddPrizeRequestBody = {
      ...restData,
      marketId: market.id,
      rewardsPerIssue: Number(data.rewardsPerIssue),
      periods: data.periods.map(p => ({
        amount: p.amount,
        startDate: renderDate(p.startDate),
        endDate: renderDate(p.endDate),
      })),
    }
    Object.entries(requestBody).forEach(([key, value]) => {
      if (typeof value === 'string' || value instanceof File) {
        fd.append(key, value)
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        fd.append(key, value.toString())
      } else if (value instanceof Object && !(value instanceof File)) {
        fd.append(key, JSON.stringify(value))
      } else if (value !== null && value !== undefined) {
        fd.append(key, String(value))
      }
    })
    addPrize(fd)
  }

  if (markets === undefined || 'error' in markets) {
    return <RequestStatus data={markets} isLoading={isMarketsLoading} />
  }

  return (
    <>
      <Typography variant="h4" component="h1" mb={2}>
        {t('common.addField', { field: 'Prize' })}
      </Typography>
      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit(onHandleSubmit)} noValidate>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => {
                navigate('/prizes-groups')
              }}
              startIcon={<ChevronLeft />}
            >
              {t('common.backListText')}
            </Button>
            <Button type="submit" color="success" variant="contained">
              {t('common.add')}
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" flexDirection="column" gap={2}>
            <Input<PrizeAddFields> name="name" label={t('common.name')} control={control} />
            <Input<PrizeAddFields>
              name="rewardsPerIssue"
              textInputProps={{ type: 'number' }}
              label={t('common.rewardsPerIssue')}
              control={control}
            />
            <Box>
              <Select<Market, PrizeAddFields>
                control={control}
                name="market"
                withText
                label={t('common.marketName')}
                elements={markets.data}
                getOptionLabel={market => market.name}
                withTextProps={{
                  isOptionEqualToValue: (option, value) => {
                    return option.id === value.id
                  },
                }}
                renderOption={(props, market) => (
                  <Box component="li" {...props} key={`${market.id}-${market.name}`}>
                    <Box>
                      {market.name}

                      {!!market.description && (
                        <Typography display="block" color="grey.400" variant="caption" lineHeight={1} fontWeight={400}>
                          {market.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
              {watch('market.description') ? (
                <Typography display="block" color="grey.400" variant="caption" lineHeight={1} fontWeight={400}>
                  {watch('market.description')}
                </Typography>
              ) : null}
            </Box>
            {translations}
            <Select<PrizeType, PrizeAddFields>
              control={control}
              name="type"
              label={t('prize.type')}
              elements={prizeTypeMock}
              renderFunction={type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              )}
            />
            {watch('market.flow') !== undefined && watch('market.flow') === 'gwp' ? (
              <Select<PeriodType, PrizeAddFields>
                control={control}
                name="period"
                label={t('prize.period')}
                elements={periodsMock}
                renderFunction={type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                )}
              />
            ) : null}
            <Input<PrizeAddFields>
              name="totalAmount"
              label={t('prize.periodTotal')}
              textInputProps={{ type: 'number' }}
              control={control}
            />
            {watch('type').includes('digital') ? (
              <FileUpload<PrizeAddFields>
                extensions={excelExt}
                type="file"
                name="file"
                label={t('prize.file')}
                control={control}
              />
            ) : null}
          </Box>
          {typeFields}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => {
                navigate('/prizes-groups')
              }}
              startIcon={<ChevronLeft />}
            >
              {t('common.backListText')}
            </Button>
            <Button type="submit" color="success" variant="contained">
              {t('common.add')}
            </Button>
          </Box>
        </form>
        {isLoading && <PageLoader />}
      </Paper>
    </>
  )
}

export default PrizeAddForm
